import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import DeleteButton from '../components/DeleteButton';

const CASE_TYPE_LABELS = {
  scam_investigation: 'Scam Investigation',
  dprk_it_worker_screening: 'DPRK IT Worker Screening',
  phishing_collection: 'Phishing Collection',
  crypto_fraud: 'Crypto Fraud',
  fake_recruiter: 'Fake Recruiter',
  malware_artifact: 'Malware Artifact',
  brand_impersonation: 'Brand Impersonation',
  general_threat_actor: 'General Threat Actor',
  client_submission: 'Client Submission',
  internal_research: 'Internal Research',
};

function Cases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get('archived') === '1';
  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    case_type: 'general_threat_actor',
    status: 'new',
    priority: 'medium',
    description: '',
    lead_investigator: '',
    client_name: '',
    risk_level: 'medium',
  });

  const loadCases = async () => {
    setLoading(true);
    try {
      const casesPath = showArchived ? '/hdp/cases?archived=1' : '/hdp/cases';
      const [casesRes, metaRes, dashboardRes] = await Promise.all([
        api.get(casesPath),
        api.get('/hdp/meta'),
        api.get('/hdp/dashboard'),
      ]);
      setCases(casesRes.data.cases || []);
      setMeta(metaRes.data);
      setDashboard(dashboardRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, [showArchived]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/hdp/cases', form);
    setShowForm(false);
    setForm({
      title: '',
      case_type: 'general_threat_actor',
      status: 'new',
      priority: 'medium',
      description: '',
      lead_investigator: '',
      client_name: '',
      risk_level: 'medium',
    });
    loadCases();
  };

  const setCaseView = (archived) => {
    if (archived) {
      setSearchParams({ archived: '1' });
    } else {
      setSearchParams({});
    }
  };

  const restoreCase = async (caseId) => {
    await api.patch(`/hdp/cases/${caseId}`, { restore: true });
    loadCases();
  };

  const highPriority = cases.filter((item) => ['high', 'critical'].includes(item.priority));
  const recentlyUpdated = [...cases].slice(0, 5);

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Cases</h1>
        <div className="header-actions">
          {!showArchived && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + New Case
          </button>
          )}
        </div>
      </header>

      <div className="main-body">
        <div className="case-view-tabs">
          <button
            type="button"
            className={`case-view-tab ${!showArchived ? 'active' : ''}`}
            onClick={() => setCaseView(false)}
          >
            Active cases
          </button>
          <button
            type="button"
            className={`case-view-tab ${showArchived ? 'active' : ''}`}
            onClick={() => setCaseView(true)}
          >
            Archived
          </button>
        </div>

        {!showArchived && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Open Cases</div>
            <div className="stat-value">{dashboard?.open_cases || 0}</div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-label">High Priority</div>
            <div className="stat-value">{highPriority.length}</div>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Evidence Items</div>
            <div className="stat-value">{dashboard?.new_evidence || 0}</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-label">IOCs Tracked</div>
            <div className="stat-value">{dashboard?.recent_iocs || 0}</div>
          </div>
        </div>
        )}

        {!showArchived && showForm && (
          <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="card-header">
              <h3 className="card-title">Create Case</h3>
            </div>
            <form className="card-body" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Case Type</label>
                  <select className="form-input" value={form.case_type} onChange={(e) => setForm({ ...form, case_type: e.target.value })}>
                    {(meta?.case_types || Object.keys(CASE_TYPE_LABELS)).map((type) => (
                      <option key={type} value={type}>{CASE_TYPE_LABELS[type] || type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    {(meta?.case_priorities || ['low', 'medium', 'high', 'critical']).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Risk Level</label>
                  <select className="form-input" value={form.risk_level} onChange={(e) => setForm({ ...form, risk_level: e.target.value })}>
                    {(meta?.risk_levels || ['low', 'medium', 'high', 'critical']).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Lead Investigator</label>
                  <input className="form-input" value={form.lead_investigator} onChange={(e) => setForm({ ...form, lead_investigator: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Client / Program</label>
                  <input className="form-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button type="submit" className="btn btn-primary">Create Case</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: showArchived ? '1fr' : '2fr 1fr', gap: 'var(--spacing-lg)' }}>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{showArchived ? 'Archived Cases' : 'Case Files'}</h3>
            </div>
            <div className="card-body">
              {showArchived && (
                <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
                  Archived cases are read-only. Restore a case to return it to the active list.
                </p>
              )}
              {loading ? (
                <p style={{ color: 'var(--text-muted)' }}>Loading cases...</p>
              ) : cases.length === 0 ? (
                <p style={{ color: 'var(--text-muted)' }}>
                  {showArchived
                    ? 'No archived cases.'
                    : 'No cases yet. Create one to begin evidence collection.'}
                </p>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Case #</th>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Evidence</th>
                        <th>IOCs</th>
                        <th>Updated</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map((item) => (
                        <tr key={item.case_id}>
                          <td><code>{item.case_number}</code></td>
                          <td>
                            <Link to={`/cases/${item.case_id}`} style={{ color: 'var(--neon-cyan)' }}>
                              {item.title}
                            </Link>
                          </td>
                          <td>{CASE_TYPE_LABELS[item.case_type] || item.case_type}</td>
                          <td><span className={`status-badge ${item.status}`}>{item.status}</span></td>
                          <td>{item.priority}</td>
                          <td>{item.evidence_count}</td>
                          <td>{item.indicator_count}</td>
                          <td>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '—'}</td>
                          <td>
                            {showArchived ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => restoreCase(item.case_id)}
                              >
                                Restore
                              </button>
                            ) : (
                              <DeleteButton
                                confirmMessage={`Archive case "${item.title}"? It will be removed from active lists.`}
                                onDelete={async () => {
                                  await api.delete(`/hdp/cases/${item.case_id}`);
                                  setCases((prev) => prev.filter((row) => row.case_id !== item.case_id));
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {!showArchived && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recently Updated</h3>
            </div>
            <div className="card-body">
              {recentlyUpdated.map((item) => (
                <div key={item.case_id} style={{ marginBottom: '12px' }}>
                  <Link to={`/cases/${item.case_id}`} style={{ color: 'var(--neon-cyan)' }}>{item.title}</Link>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    {item.case_number} · {item.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Cases;
